<script setup lang="ts">
  import { Library } from "~/entities/Library";
  import { PlanInfo } from "~/entities/PlanInfo";
  import { DraggableItem } from "~/entities/DraggableItem";
  import { useSnackBarStore } from "~/stores/SnackBarStore";
  import { useLibraryStore } from "~/stores/LibraryStore";
  import { useLoginStore } from "~/stores/LoginStore";
  import { usePlanStore } from "~/stores/PlanStore";
  import { useHistoryStore } from "~/stores/HistoryStore";
  import { usePlanManageStore } from "~/stores/PlanManageStore";

  interface Props {
    planInfo: PlanInfo;
    libraries: Library[];
    selectedId: string;
  }
  const props = defineProps<Props>();

  interface Emits {
    (event: "onClickItem", id: string): void;
  }
  const emit = defineEmits<Emits>();

  const libraryStore = useLibraryStore();
  const loginStore = useLoginStore();
  const planStore = usePlanStore();
  const historyStore = useHistoryStore();
  const planManageStore = usePlanManageStore();

  const showSelection = (id: string) => {
    // ルーム変更時にはqueryを変更して対応する
    emit("onClickItem", id);
    historyStore.to({ query: { ...useRoute().query, content: id, showDetail: "true" } });
  };

  const onClickItem = (id: string) => {
    showSelection(id);

    // live, movie,document,article,未ログイン時は既読処理を行わない
    const contentIds = ["live", "movie", "document", "article"];
    if (contentIds.includes(id) || loginStore.isLogin() === false) {
      return;
    }

    // 閲覧フラグを立てる。閲覧回数にも使われているので毎回呼ぶ
    libraryStore.browseLibrary(id);
    localLibraries.value.forEach(item => {
      if (item.id === id) {
        item.hasUnread = false;
      }
    });
    const unreadCount = localLibraries.value.filter(item => item.hasUnread).length;
    if (unreadCount === 0) {
      planStore.fetchOne(useRoute().params.id as string);
    }
  };

  const saveMenuSort = async (data: Record<string, number>) => {
    await planManageStore.updateMenuSort(props.planInfo.id, data);
  };

  // ローカルメニュー
  const localMenus = ref<DraggableItem[]>(
    [
      {
        id: "live",
        label: "ライブ一覧",
        canView: true,
        hasUnread: props.planInfo.hasUnreadArchive || props.planInfo.hasUnreadSchedule || props.planInfo.hasUnreadLive,
        imgPath: "assets/images/icon/menu/plan_live.svg",
        isUserIcon: false,
        isOwner: false,
        sort: props.planInfo.menuSort.liveMovie,
        onClick: onClickItem,
      },
      {
        id: "movie",
        label: "動画一覧",
        canView: true,
        hasUnread: props.planInfo.hasUnreadMovie,
        imgPath: "assets/images/icon/menu/plan_movie.svg",
        isUserIcon: false,
        isOwner: false,
        sort: props.planInfo.menuSort.movie,
        onClick: onClickItem,
      },
      {
        id: "document",
        label: "資料一覧",
        canView: true,
        hasUnread: props.planInfo.hasUnreadDocument,
        imgPath: "assets/images/icon/menu/plan_document.svg",
        isUserIcon: false,
        isOwner: false,
        sort: props.planInfo.menuSort.document,
        onClick: onClickItem,
      },
      {
        id: "article",
        label: "記事一覧",
        canView: true,
        hasUnread: props.planInfo.hasUnreadArticle,
        imgPath: "assets/images/icon/menu/article.svg",
        isUserIcon: false,
        isOwner: false,
        sort: props.planInfo.menuSort.article,
        onClick: onClickItem,
      },
    ]
      .filter(item => item !== null)
      .sort((a, b) => a.sort - b.sort) as DraggableItem[],
  );

  watch(
    () => localMenus.value,
    newVal => {
      if (!props.planInfo.isOwner) {
        return;
      }
      const data: Record<string, number> = {};
      for (const [index, item] of newVal.entries()) {
        data[item.id] = index + 1;
      }
      saveMenuSort(data);
    },
  );

  watch(
    () => props.planInfo,
    newVal => {
      localMenus.value = localMenus.value.map(item => {
        if (item.id === "live") {
          item.hasUnread = newVal.hasUnreadArchive || newVal.hasUnreadSchedule || newVal.hasUnreadLive;
        } else if (item.id === "movie") {
          item.hasUnread = newVal.hasUnreadMovie;
        } else if (item.id === "document") {
          item.hasUnread = newVal.hasUnreadDocument;
        } else if (item.id === "article") {
          item.hasUnread = newVal.hasUnreadArticle;
        }
        return item;
      });
    },
  );

  // セレクション
  const convertGroupTalkRoom = (library: Library) => {
    return {
      id: String(library.libraryId),
      label: library.libraryTitle,
      canView: true,
      hasUnread: library.isUnread,
      imgPath: library.isComplete
        ? "assets/images/icon/menu/circle-check-blue.svg"
        : "assets/images/icon/menu/circle-check.svg",
      isUserIcon: false,
      isOwner: false,
      onClick: onClickItem,
    } as DraggableItem;
  };
  const localLibraries = computed(() => props.libraries.map(convertGroupTalkRoom));
  const localLibrariesModel = computed<DraggableItem[]>({
    get: () => localLibraries.value,
    set: async value => {
      await libraryStore.updateDisplaySort(
        props.planInfo.id,
        value.map(item => item.id),
      );
    },
  });

  const snackBarStore = useSnackBarStore();
  const clickNewSelection = () => {
    if (localLibraries.value.length >= 200) {
      snackBarStore.setError("講師セレクションは最大200個までです");
      return;
    }
    historyStore.to(`/plan/${props.planInfo.id}/library/create`);
  };

  // 各講師セレクションの開閉状態
  // いまは固定のデフォルト値だが、将来的には localStorage などで保存する
  const isLocalLibrariesOpen = ref(true);
</script>

<template>
  <MoleculesContainerMenuList :has-extra="props.planInfo.isOwner">
    <!-- 開閉パネル無効な場合 -->
    <MoleculesDraggableList
      v-model:items="localMenus"
      :selected-id="selectedId"
      :can-drag="props.planInfo.isOwner"
    ></MoleculesDraggableList>

    <!-- 開閉パネル有効な場合 -->
    <MoleculesExpansionPanel
      v-if="localLibrariesModel.length > 0"
      v-model:is-open="isLocalLibrariesOpen"
      :has-unread="localLibraries.filter(item => item.hasUnread).length > 0"
    >
      <template #title>講師セレクション</template>
      <template #default>
        <MoleculesDraggableList
          v-model:items="localLibrariesModel"
          :selected-id="selectedId"
          :can-drag="props.planInfo.isOwner"
        ></MoleculesDraggableList>
      </template>
    </MoleculesExpansionPanel>

    <template #extra>
      <v-btn v-if="props.planInfo.isOwner" class="add-library d-flex align-center" @click="clickNewSelection">
        <v-icon icon="$mdiPlus" size="large" />
        講師セレクション作成
      </v-btn>
    </template>
  </MoleculesContainerMenuList>
</template>

<style lang="scss" scoped>
  @import "assets/media.scss";
  .add-library {
    cursor: pointer;
    width: calc(100% - 20px);
    height: 40px;
    border-radius: 40px;
    background-color: rgba(50, 50, 50, 1);
    margin: 16px auto 10px;
    @include display_sp {
      margin: 16px auto 30px;
    }
    padding: 0 10px;
    font-size: 16px;
    font-weight: 700;
    line-height: 1.8em;
  }
</style>
